"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useRef, useEffect, useState } from "react";
import gsap from "gsap";

import MainNav from "@components/global/main-nav";
import GSAPAnimationWrapper from "@components/global/gsap-animation-wrapper";
import AnimatedLogo from "@components/global/animated-logo";
import { useHomeIntro } from "@/contexts/home-intro-context";

export default function Header() {
  const pathname = usePathname();
  const isHomePage = pathname === "/";
  const { showHomeNav } = useHomeIntro();
  const logoRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      let threshold = 50;

      // On About page, wait for pinned section to finish before shrinking header
      if (pathname === "/about") {
        const isMobile = window.innerWidth < 768;
        // Match the pin duration from about-client.tsx (2800 mobile, 6200 desktop)
        threshold = isMobile ? 2800 : 6200;
      }

      setIsScrolled(window.scrollY > threshold);
    };

    window.addEventListener("scroll", handleScroll);
    // Initial check
    handleScroll();
    
    return () => window.removeEventListener("scroll", handleScroll);
  }, [pathname]);

  useEffect(() => {
    if (isHomePage && showHomeNav && logoRef.current && navRef.current) {
      // Fade in logo
      gsap.to(logoRef.current, {
        opacity: 1,
        duration: 0.8,
        ease: "power2.out",
      });

      // Fade in nav 0.2s later
      gsap.to(navRef.current, {
        opacity: 1,
        duration: 0.8,
        delay: 0.2,
        ease: "power2.out",
      });
    }
  }, [isHomePage, showHomeNav]);

  const showHeader = !isHomePage || showHomeNav;

  return (
    <header className="main-header fixed top-0 left-0 w-full z-[9999]">
      <div 
        className={`max-w-container mx-auto flex justify-between items-center px-[10px] md:px-[15px] transition-all duration-300 ${
          isScrolled ? "py-[0.2rem]" : "py-6"
        }`}
      >
        {showHeader && (
          <>
            <div ref={logoRef} style={isHomePage ? { opacity: 0 } : undefined}>
              <Link href="/">
                <GSAPAnimationWrapper>
                  <AnimatedLogo className="w-[3.5rem] md:w-[5.2rem]" />
                </GSAPAnimationWrapper>
              </Link>
            </div>
            <div 
              ref={navRef} 
              style={isHomePage ? { opacity: 0 } : undefined}
              className={`transition-all duration-300 rounded-full px-6 py-2 ${
                isScrolled 
                  ? "bg-black/40 backdrop-blur-md shadow-sm" 
                  : "bg-transparent"
              }`}
            >
              <MainNav />
            </div>
          </>
        )}
      </div>
    </header>
  );
}
